import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Search, FileText, Calendar, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SearchModal({ isOpen, onClose, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
      performSearch(initialQuery);
    }
  }, [initialQuery]);

  // Auto-search when query changes (with debounce)
  useEffect(() => {
    if (!isOpen) return;

    const debounceTimer = setTimeout(() => {
      if (query && query.length >= 2) {
        performSearch(query);
      } else {
        setResults([]);
        setTotal(0);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [query, isOpen]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setTotal(0);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data.results || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(query);
  };

  const handleResultClick = (url) => {
    navigate(url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center pt-20 px-4 transition-all animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="bg-[#0E6D2B] text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Search ICAR-CISH</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 border-b">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for content, news, publications..."
              className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-[#0E6D2B] focus:outline-none"
              autoFocus
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-[#0E6D2B] text-white rounded-lg hover:bg-[#0a5a23]"
            >
              <Search className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Results */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {loading && (
            <div className="text-center py-12 text-gray-500">
              Searching...
            </div>
          )}

          {!loading && query && total === 0 && (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No results found for "{query}"</p>
              <p className="text-gray-500 text-sm mt-2">Try different keywords or browse our menu</p>
            </div>
          )}

          {!loading && total > 0 && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Found {total} result{total !== 1 ? 's' : ''} for <strong>"{query}"</strong>
              </p>

              <div className="space-y-3">
                {results.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => handleResultClick(result.url)}
                    className="border rounded-lg p-4 hover:bg-gray-50 hover:border-[#0E6D2B] cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-[#0E6D2B] bg-opacity-10 text-[#0E6D2B] text-xs font-medium rounded">
                            {result.type}
                          </span>
                          {result.date && (
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <Calendar className="w-3 h-3" />
                              {result.date}
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-800 mb-1 hover:text-[#0E6D2B]">
                          {result.title}
                        </h3>
                        {result.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {result.description}
                          </p>
                        )}
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && !query && (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p>Enter keywords to search across the website</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
